import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { getMmseLicensingEvidenceById } from '../lib/mmseLicensingEvidence';
import { FANOUT_PAGES } from '../lib/site';

const page = FANOUT_PAGES.find((p) => p.path === '/mini-mental-telessaude');
const reproducao = getMmseLicensingEvidenceById('par-licensing-team');
const permissoes = getMmseLicensingEvidenceById('par-mmse-permissions');

const ANSWER =
  'O Mini Mental pode ser discutido por telessaúde, mas a aplicação remota não é a mesma aplicação presencial. Parte do teste depende de materiais que o paciente manuseia ou escreve e de observação direta da execução. Quando a modalidade muda, o escore pode deixar de ser comparável aos pontos de corte padrão e precisa ser registrado como aplicação modificada. Mostrar o instrumento em tela ou enviá-lo como arquivo também pode depender de autorização do detentor dos direitos.';

const REFERENCES = [
  `${permissoes.authority}. ${permissoes.title}. Disponível em: ${permissoes.url}.`,
  `${reproducao.authority}. ${reproducao.title}. Disponível em: ${reproducao.url}.`
];

const RELATED = [
  { to: '/como-aplicar', label: 'Como aplicar o Mini Mental', note: 'padronização e condições gerais da aplicação presencial.' },
  { to: '/formulario-e-direitos-de-uso', label: 'Existe PDF gratuito do Mini Mental?', note: 'o que a editora informa sobre reprodução e uso.' },
  { to: '/erros-de-aplicacao', label: 'Erros comuns de aplicação', note: 'o que compromete a comparabilidade, em qualquer modalidade.' },
  { to: '/interpretacao-dos-resultados', label: 'Como interpretar o resultado', note: 'leia o escore junto do contexto clínico e da modalidade usada.' }
];

function InlineLink({ to, children }) {
  return <Link to={to} className=" text-[var(--primary)] hover:underline">{children}</Link>;
}

export default function MiniMentalTelessaude() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="o-que-muda">
        <h2 id="o-que-muda" className="text-2xl mb-5">O que muda quando a aplicação é remota?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Na aplicação presencial, quem aplica controla o ambiente, vê exatamente como a pessoa executa cada tarefa e registra as respostas no momento em que acontecem. Na modalidade remota, essas garantias diminuem: a câmera mostra só parte da cena, a iluminação e o áudio variam, e não é possível confirmar quem está ao lado do paciente.
        </p>
        <p className="text-[var(--text-muted)]">
          Isso não invalida a conversa clínica por vídeo. Significa que o escore obtido nessa condição deve ser descrito como o que realmente foi: uma aplicação em modalidade diferente da padrão.
        </p>
      </section>

      <section aria-labelledby="tarefas-materiais">
        <h2 id="tarefas-materiais" className="text-2xl mb-5">Quais partes do teste são mais difíceis de padronizar à distância?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          As tarefas que exigem escrever, desenhar ou manipular material impresso são as mais sensíveis à modalidade. Nelas, o avaliador depende do que aparece na câmera, da qualidade do papel e da caligrafia, e não consegue checar com precisão o gesto e o tempo de execução.
        </p>
        <p className="text-[var(--text-muted)]">
          Tarefas puramente verbais também mudam de caráter: a audição, o som do ambiente e a presença de outra pessoa que responde por ela afetam o resultado. Antes de comparar qualquer número remoto com um presencial, registre quais tarefas foram realmente observadas.
        </p>
      </section>

      <section aria-labelledby="direitos-tela">
        <h2 id="direitos-tela" className="text-2xl mb-5">Posso mostrar o instrumento na tela ou enviar um arquivo?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Esta é a pergunta que mais exige cuidado. A editora detentora do MMSE informa que a reprodução, a fotocópia e a tradução dependem de permissão, e que uma versão modificada precisa de autorização própria. Exibir o estímulo em tela ou enviá-lo ao paciente pode caracterizar reprodução.
        </p>
        <p className="text-[var(--text-muted)]">
          Por isso este site não reproduz nem descreve os estímulos do teste. Para entender quais canais são autorizados, veja <InlineLink to="/formulario-e-direitos-de-uso">o que a editora informa sobre o formulário e os direitos de uso</InlineLink>. Uso sem autorização não se resolve com boa intenção clínica.
        </p>
      </section>

      <section aria-labelledby="registro-remoto">
        <h2 id="registro-remoto" className="text-2xl mb-5">Como registrar uma aplicação remota?</h2>
        <ol className="space-y-3 text-[var(--text-muted)] list-decimal pl-5">
          <li>registre que a aplicação foi feita por telessaúde, com data e plataforma usada;</li>
          <li>registre quem estava presente no ambiente do paciente e qual foi o papel dessa pessoa;</li>
          <li>registre o material que o paciente tinha à mão e se ele era o autorizado;</li>
          <li>marque as tarefas que não puderam ser observadas diretamente;</li>
          <li>marque o resultado como aplicação modificada, sem compará-lo automaticamente aos pontos de corte padrão;</li>
          <li>guarde os dados do paciente na plataforma que cumpre a proteção de dados aplicável.</li>
        </ol>
      </section>

      <section aria-labelledby="comparar-modalidades">
        <h2 id="comparar-modalidades" className="text-2xl mb-5">Dá para comparar um resultado remoto com um presencial?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Com cautela. Uma diferença de pontos entre uma aplicação presencial e uma remota pode vir da modalidade, e não da evolução do paciente. Por isso, a regra mais útil é manter a mesma modalidade ao longo do acompanhamento: se o primeiro teste foi remoto, os seguintes também devem ser.
        </p>
        <p className="text-[var(--text-muted)]">
          Quando for inevitável mudar de modalidade, trate a comparação como sinal para investigar, e não como medida de declínio. Para interpretar variações entre aplicações, veja <InlineLink to="/acompanhamento-e-queda-de-pontos">o que significa uma queda de pontos no acompanhamento</InlineLink>.
        </p>
      </section>

      <section aria-labelledby="quando-ajuda">
        <h2 id="quando-ajuda" className="text-2xl mb-5">Quando a aplicação remota ainda ajuda?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Para triagem inicial em locais sem acesso presencial, para decidir quem precisa de avaliação completa e para orientar a família sobre quando procurar atendimento presencial. Nesses casos, o resultado orienta o próximo passo, mas não fecha diagnóstico.
        </p>
        <p className="text-[var(--text-muted)]">
          Antes de usar o escore para qualquer decisão, leia <InlineLink to="/interpretacao-dos-resultados">como interpretar a pontuação</InlineLink> e a referência de escolaridade em <InlineLink to="/escolaridade">Mini Mental por escolaridade</InlineLink>.
        </p>
      </section>

      <section aria-labelledby="responsabilidade">
        <h2 id="responsabilidade" className="text-2xl mb-5">Quem responde pela aplicação remota?</h2>
        <p className="text-[var(--text-muted)]">
          O profissional responsável pelo atendimento responde pela aplicação, pela interpretação e pelo registro. A prática de telemedicina segue as regras do conselho profissional e a regulamentação vigente, que devem ser consultadas antes de adotar o modelo. Este texto é informativo e não substitui essa verificação.
        </p>
      </section>

      <section aria-labelledby="fluxo-remoto">
        <h2 id="fluxo-remoto" className="text-2xl mb-5">Fluxo prático</h2>
        <div className="p-5 border border-[var(--border)] rounded-2xl bg-[var(--surface-soft)] font-bold leading-relaxed">
          verificar direitos de uso → registrar a modalidade → observar o que for possível → marcar o que não foi observado → interpretar sem comparação automática → manter a mesma modalidade no acompanhamento
        </div>
        <p className="text-[var(--text-muted)] mt-5">
          Para a aplicação presencial, que serve de padrão de comparação, consulte <InlineLink to="/como-aplicar">como aplicar o Mini Mental</InlineLink>.
        </p>
      </section>
    </ArticleLayout>
  );
}
